import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import AnalysisTaskPopup from "components/Config/AnalysisTaskPopup"
import { analysisTasks, visual } from "state_management"

// Route-independent host for a PROGRAMMATIC "open this analysis task" request (RR8 #4061).
//
// `visual.activeAnalysisTaskId` is the app-wide "open this task" intent, set by
// `limsReviews.openReviewAnalysis` (Sample View's "Go to analysis", a Reviews card, the QA worklist
// deep-link) and by `visual.openAnalysisTask`. It used to be consumed ONLY by `ScheduleElement`,
// which:
//   * exists only while the PLAN is mounted, and
//   * only reacted when the target task happened to be rendered in that cell's member+date.
// So every caller had to route to the plan first, and if the task never rendered the flag was never
// cleared — leaving the app-root `AnalysisNavOverlay` stuck on "Opening analysis…" (and, because the
// whole `visual` slice was persisted to localStorage, stuck across reloads too).
//
// This component owns that intent instead. It is mounted at the app root, so the dialog opens over
// whatever the user is looking at — no route change, and no dependency on the plan rendering the task.
// `ScheduleElement` keeps its own click-to-open path for tiles on the plan; both render the same
// `AnalysisTaskPopup`.
//
// The dialog opens IMMEDIATELY on the request and shows a spinner in its own body until the asset
// lookup finishes — a click must produce something visible at once. `handlingRef` stops the effect
// from starting a second lookup while the first is running (its deps include `analysisTasks`, which
// any background refetch rebuilds), and the request is cleared as soon as the dialog is up — or if
// the task cannot be resolved at all.
export default function AnalysisTaskHost() {
  const dispatch = useDispatch()
  const requestedTaskId = useSelector((state) => state.visual.activeAnalysisTaskId)
  const requestedFocusQc = useSelector((state) => state.visual.analysisTaskFocusQc)
  const allAnalysisTasks = useSelector((state) => state.analysisTasks)

  const isViewOnly = useSelector((state) => state.auth.isViewOnly)
  const isMember = useSelector((state) => state.auth.isMember)
  const allowTaskTransfer = useSelector((state) => state.teamStatus.allowTaskTransfer)
  const authMemberId = useSelector((state) => state.auth.memberId)
  const authMemberShowOnPlan = useSelector((state) =>
    authMemberId && state.members[authMemberId] ? state.members[authMemberId].showOnPlan : true
  )

  // What this host currently has open. `task` is a snapshot for the same reason ScheduleElement keeps
  // one (SPEC-028): a background refetch rebuilds `analysisTasks` and can drop the open task mid-work,
  // which must not unmount the dialog underneath the user.
  const [open, setOpen] = useState(null) // { taskId, task, focusQc, initialTab }
  // True between opening the dialog and the asset lookup resolving; the dialog shows a spinner.
  const [assetsLoading, setAssetsLoading] = useState(false)
  const [bookedAndFreeAssets, setBookedAndFreeAssets] = useState({ bookedAssets: [], freeAssets: [] })
  // The request currently being resolved, so the effect is not re-entrant.
  const handlingRef = useRef(null)

  const liveTask = useSelector((state) => (open && state.analysisTasks[open.taskId]) || null)

  const readOnlyFor = (task) =>
    isViewOnly ||
    (isMember && !allowTaskTransfer && (!authMemberShowOnPlan || !(task.memberIds || []).includes(authMemberId)))

  const close = () => {
    setOpen(null)
    setAssetsLoading(false)
    setBookedAndFreeAssets({ bookedAssets: [], freeAssets: [] })
    dispatch(visual.setAnalysisTaskDialogOpen(false))
  }

  useEffect(() => {
    if (!requestedTaskId) return
    if (handlingRef.current === requestedTaskId) return // already resolving this one
    const task = allAnalysisTasks[requestedTaskId]
    // No such task in state: clear the intent rather than sit on it. `openReviewAnalysis` already
    // refuses (with a message) when the analysis has no task on the plan, so this is the belt to that
    // braces — an intent we cannot serve must never survive, or the overlay hangs.
    if (!task) {
      dispatch(visual.clearActiveAnalysisTask())
      return
    }
    handlingRef.current = requestedTaskId
    const focusQc = requestedFocusQc
    const needsAssets = !readOnlyFor(task)
    // Show the dialog FIRST, then fill it in. Deferred off the effect body so this never setStates
    // synchronously in an effect (the idiom ScheduleElement used for the same reason).
    Promise.resolve().then(() => {
      handlingRef.current = null
      setAssetsLoading(needsAssets)
      dispatch(visual.setAnalysisTaskDialogOpen(true))
      setOpen({
        taskId: requestedTaskId,
        task,
        focusQc,
        // focusQc → land on the Edit Analysis-task view; otherwise the samples tab. Mirrors the
        // behaviour ScheduleElement had for this path.
        initialTab: focusQc ? "TASK" : "SAMPLE",
      })
      dispatch(visual.clearActiveAnalysisTask())
    })

    if (needsAssets) {
      dispatch(analysisTasks.getBookedFreeAssets(requestedTaskId, task))
        .then((res) => setBookedAndFreeAssets(res))
        // An asset lookup failure must not leave the dialog spinning forever — fall through to the
        // form with no availability data rather than trapping the user.
        .catch(() => setBookedAndFreeAssets({ bookedAssets: [], freeAssets: [] }))
        .finally(() => setAssetsLoading(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    requestedTaskId,
    requestedFocusQc,
    allAnalysisTasks,
    dispatch,
    isViewOnly,
    isMember,
    allowTaskTransfer,
    authMemberId,
    authMemberShowOnPlan,
  ])

  if (!open) return null

  const task = liveTask || open.task

  return (
    <AnalysisTaskPopup
      taskId={open.taskId}
      task={task}
      focusQc={open.focusQc}
      initialTab={open.initialTab}
      bookedAndFreeAssets={bookedAndFreeAssets}
      loading={assetsLoading}
      disabled={readOnlyFor(task)}
      onClose={close}
    />
  )
}
